import React, { useMemo } from 'react';
import { PapiDeviation } from '@wix/da-papi-types';
import ddt from '@wix/da-ddt';
import { Url } from '@wix/da-url';
import {
  PureBiData,
  BuyPremiumDownloadClickBiEvent,
  ClickDownloadButtonBiEvent,
} from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import BuyButton from '@wix/da-ds/pkg/Buttons/BuyButton';
import { Props as BuyButtonProps } from '@wix/da-ds/pkg/Buttons/BuyButton/BuyButton';
import DownloadIcon from '@wix/da-ds/pkg/Icons/24x24/Download';
import PriceWithDiscount from '../../../../../../PriceWithDiscount';
import { useContentFilter } from '../../../../../../utils/hooks/useContentFilter';
import { useIsCurrentUserDeviationAuthor } from '../../../../../../utils/hooks/useIsCurrentUserDeviationAuthor';
import { getDeviationSaleDetails } from '../../../../../../utils/deviations';
import { useRestrictToLoggedIn } from '../../../../../../utils/hooks/useRestrictToLoggedIn';
import { LoggedOutReturnType } from '../../../../../../redux/loggedout';
import { getDeviationPremiumDownload } from '../../../../../../ActionBar/buttons/DownloadButton/permissions';
import { useTranslation } from 'react-i18next';
import { formatDollars } from '../../../../../../utils/formatting';

const logger = ddt.createLogger('downloadbutton');

interface Props {
  deviation: PapiDeviation;
  onDownload?: () => void;
}

const BuyPremiumContentButton: React.FC<Props> = ({ deviation, onDownload }) => {
  const { t } = useTranslation();
  const { isLoggedIn, sendUserToJoinPage } = useRestrictToLoggedIn();
  const { contentFilter, pushUserSettingsModal } = useContentFilter();
  const { eligibleDiscount, hasDiscounts } = useMemo(
    () => getDeviationSaleDetails(deviation),
    [deviation]
  );

  logger.log('BuyPremiumContentButton, deviation:', deviation);
  const isOwner = useIsCurrentUserDeviationAuthor(deviation);
  const { download, permission } = getDeviationPremiumDownload(deviation, isOwner);
  logger.log({ isOwner, download, permission });

  if (!download) {
    logger.log('has no downloads');
    return null;
  }

  const isPurchasable = permission === 'purchasable';

  let buttonProps: BuyButtonProps = {
    biData: isPurchasable
      ? PureBiData<BuyPremiumDownloadClickBiEvent>({
          evid: 349,
          sectionname: 'side_rail',
          itemprice: download.downloadDollarPrice.toFixed(2),
          itemid: deviation.deviationId,
          typeid: deviation.typeId,
        })
      : PureBiData<ClickDownloadButtonBiEvent>({
          evid: 350,
          ispaiddownload: 1,
          deviationid: deviation.deviationId,
          itemid: deviation.deviationId,
          typeid: deviation.typeId,
          sectionname: 'side_rail',
        }),
    icon: permission === 'downloadable' ? DownloadIcon : undefined,
    variant: 'solid',
    size: 'medium',
    rounded: true,
    fillContainerWidth: true,
  };

  const checkoutUrl = deviation.extended?.pcp?.subproductId
    ? Url.checkoutLink(deviation.extended?.pcp?.subproductId)
    : undefined;

  const buttonLabel = t(
    permission === 'downloadable'
      ? 'sidebar.pcpInfo.button.download'
      : 'sidebar.pcpInfo.button.purchase',
    { dollarPrice: formatDollars(deviation?.pcp?.downloadDollarPrice ?? 0) }
  );

  // send anon to sisu
  if (!isLoggedIn) {
    return (
      <BuyButton
        {...buttonProps}
        onClick={() =>
          sendUserToJoinPage(
            ReferralType.ANON_DOWNLOAD,
            deviation.url,
            LoggedOutReturnType.JOIN_ONLY
          )
        }
      >
        {buttonLabel}
      </BuyButton>
    );
  }

  // still needs to purchase at checkout
  if (isPurchasable && !!checkoutUrl) {
    // we may need them to update their mature content settings before
    // sending them to checkout or ponies will 404 the page
    const needsBrowsingModeChange = deviation.isMature && contentFilter !== 'mature';
    buttonProps = needsBrowsingModeChange
      ? { ...buttonProps, onClick: () => pushUserSettingsModal() }
      : { ...buttonProps, href: checkoutUrl };

    // is it already purchased?
  } else if (permission === 'downloadable') {
    buttonProps = {
      ...buttonProps,
      href: download.url,
      target: '_blank',
      download: true,
      onClick: onDownload,
    };
  }

  const showDiscount = isPurchasable && hasDiscounts;

  return (
    <BuyButton {...buttonProps}>
      {!showDiscount && buttonLabel}
      {showDiscount && (
        <PriceWithDiscount
          dollarAmount={download.downloadDollarPrice}
          eligibleDiscount={eligibleDiscount}
          prefix={t('sidebar.exclusives.buttons.buyer.buyFor')}
          size={buttonProps.size === 'small' ? 'small' : 'medium'}
          reversedTheme
        />
      )}
    </BuyButton>
  );
};
BuyPremiumContentButton.displayName = 'BuyPremiumContentButton';

export default BuyPremiumContentButton;
